import React, { useState } from "react";
import styled from "styled-components";
import { Plus, Search } from "react-feather";
import {
  Badge,
  Banner,
  Button,
  Input,
  Section,
  Spinner,
  Table,
} from "@cinesys/ui/components";
import { useQuery } from "react-query";
import { useParams } from "react-router-dom";
import ApiService from "../../services/ApiService";
import { Status } from "./PluginInstanceEditScreen";
import ComponentTypeBadge from "../configurePluginOrScript/ComponentTypeBadge";
import { isUserVisibleComponent } from "../configurePluginOrScript/utils";
import Fuse from "fuse.js";
import { formatNumber } from "../shared/utils";

interface Props {
  editedPluginInstances: Record<string, PluginInstanceItem>;
  statuses: Record<string, Status>;
}

interface PluginInstanceListResponse {
  pluginInstances: PluginInstance[];
  legacyComponents: LegacyComponent[];
}

interface LegacyComponent {
  type: string;
  id: string;
  name: string;
  enabled: boolean;
}

interface PluginInstance {
  id: string;
  title: string;
  label?: string;
  development: boolean;
  // true when every toggleable component is disabled — the row is greyed out (computed backend-side)
  disabled?: boolean;
  components: {
    type: string;
    title: string;
  }[];
  currentMonthCount?: number;
  lastMonthCount?: number;
}

const StyledBadge = styled(Badge)`
  margin-right: 10px;
`;
const ComponentWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const Label = styled.span`
  color: var(--gray100);
  padding-top: 5px;
`;
const Count = styled.span<{ $empty: boolean; $large?: boolean }>`
  color: ${(props) => (props.$empty ? "var(--gray100)" : "var(--gray500)")};
  font-weight: ${(props) => (props.$large ? "bold" : "normal")};
`;
const Title = styled.span`
  display: flex;
  flex-direction: column;
`;
const TitleText = styled.span<{ $development?: boolean; $disabled?: boolean }>`
  color: ${(props) =>
    props.$development
      ? "var(--orange300)"
      : props.$disabled
      ? "var(--gray100)"
      : "var(--gray500)"};
`;

const Divider = styled.div`
  height: 1px;
`;

const SearchWrap = styled.div`
  padding: 10px;
`;

const PluginInstanceListRow = (props: {
  pluginInstance: PluginInstance;
  status?: Status;
}) => {
  const { pluginInstance } = props;
  return (
    <>
      <Title>
        <TitleText
          $development={pluginInstance.development}
          $disabled={pluginInstance.disabled}
        >
          {pluginInstance.title}
        </TitleText>
        {pluginInstance.label && <Label>{pluginInstance.label}</Label>}
      </Title>
      <ComponentWrapper>
        {/* {pluginInstance.development && <StyledBadge color="var(--orange100)" label="Dev Mode" />} */}
        {pluginInstance.components
          .filter(({ type }) => isUserVisibleComponent(type))
          .map(({ type }, index) => (
            <ComponentTypeBadge type={type} key={index} />
          ))}
      </ComponentWrapper>
      <Count
        $empty={!pluginInstance.lastMonthCount}
        $large={(pluginInstance.lastMonthCount ?? 0) > 1000}
      >
        {formatNumber(pluginInstance.lastMonthCount) ?? "-"}
      </Count>
      <Count
        $empty={!pluginInstance.currentMonthCount}
        $large={(pluginInstance.currentMonthCount ?? 0) > 1000}
      >
        {formatNumber(pluginInstance.currentMonthCount) ?? "-"}
      </Count>
    </>
  );
};

const LegacyComponentListRow = (props: {
  legacyComponent: LegacyComponent;
}) => {
  const { legacyComponent } = props;
  return (
    <>
      <ComponentWrapper>
        <StyledBadge color="var(--red100)" label="Legacy" />
        <TitleText $disabled={!legacyComponent.enabled}>
          {legacyComponent.name}
        </TitleText>
      </ComponentWrapper>
      <ComponentWrapper>
        <ComponentTypeBadge type={legacyComponent.type} />
      </ComponentWrapper>
    </>
  );
};

const PluginInstanceList = (props: Props) => {
  const { systemDomainId } = useParams();
  const { editedPluginInstances, statuses } = props;
  const [search, setSearch] = useState("");

  const { data, isLoading, isError } = useQuery({
    queryKey: ["pluginInstanceList", systemDomainId],
    queryFn: async () => {
      const response = await ApiService.fetch<{
        data: PluginInstanceListResponse;
      }>("GET", `/api/plugin_instance/${systemDomainId}`);
      return response.data;
    },
  });

  const pluginConfigQuery = useQuery({
    queryKey: ["configList", "plugin"],
    queryFn: async () => {
      const response = await ApiService.fetch<{ data: PluginConfigItem[] }>(
        "GET",
        `/api/config/plugin`
      );
      return response.data;
    },
    enabled: !!Object.keys(editedPluginInstances).length,
  });

  const pluginInstances: (PluginInstance | LegacyComponent)[] = [
    ...(data?.pluginInstances ?? []),
    ...Object.keys(editedPluginInstances)
      .filter((key) => !data?.pluginInstances.some(({ id }) => id === key))
      .map((key) => {
        const config = pluginConfigQuery.data?.find(
          ({ id }) => id === editedPluginInstances[key].pluginConfigId
        );
        return {
          id: key,
          title: config?.title ?? "Unknown plugin",
          components: config?.components ?? [],
          development: false,
          disabled: false,
          needsUpdate: false,
          new: true,
        };
      }),
    ...(data?.legacyComponents ?? []),
  ];

  let rows = pluginInstances ?? [];
  if (search) {
    const fuse = new Fuse(rows ?? [], {
      keys: ["title", "name"],
      ignoreLocation: true,
      threshold: 0.2,
      shouldSort: false,
    });

    rows = fuse.search(search).map((result) => result.item);
  }

  const now = new Date();
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);

  const currentMonth = now.toLocaleDateString("en-US", { month: "short" });
  const previousMonth = prev.toLocaleDateString("en-US", { month: "short" });

  return isLoading ? (
    <Spinner />
  ) : isError ? (
    <Banner error="Error loading" />
  ) : (
    <div>
      <Section
        title="Installed Plugins"
        buttons={<Button icon={Plus} iconSize={19} link="new" />}
      >
        <SearchWrap>
          <Input
            value={search}
            onChange={setSearch}
            placeholder="Search"
            icon={Search}
            clearable
          />
        </SearchWrap>
        <Divider />
        <Table
          style={
            {
              "--tableHeaderBg": "transparent",
              "--tableHeaderBorderBottom": "1px solid var(--dark300)",
              "--tableHeaderShadow": "none",
            } as React.CSSProperties
          }
          columnGrid
          columns={[
            { width: "1fr", title: "Title" },
            { width: "1fr", title: "Components" },
            { width: "45px", title: previousMonth },
            { width: "45px", title: currentMonth },
          ]}
          rows={
            rows.map((pluginInstance) => ({
              id: pluginInstance.id,
              link:
                "type" in pluginInstance
                  ? `legacy/${pluginInstance.id}`
                  : `${pluginInstance.id}`,
              element: () =>
                "type" in pluginInstance ? (
                  <LegacyComponentListRow
                    key={pluginInstance.id}
                    legacyComponent={pluginInstance}
                  />
                ) : (
                  <PluginInstanceListRow
                    key={pluginInstance.id}
                    pluginInstance={pluginInstance}
                    status={statuses[pluginInstance.id]}
                  />
                ),
            })) ?? []
          }
          rowHeight={40}
          autoHeight={{ twoColumn: true }}
        />
      </Section>
    </div>
  );
};

export default PluginInstanceList;
